import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, ShieldCheck, Phone, ArrowRight, Building2, IdCard, Calendar, MapPin, Wallet, Briefcase, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { getSessionId, setUserId } from "../lib/utm";
import { initMetaPixel, trackLead } from "../lib/pixel";
import { toast } from "sonner";
import BrandHeader, { normalizeColor, safeButtonColor } from "./BrandHeader";

/**
 * CreditSea Smart deep-integration dialog.
 *
 * Two-step form:
 *   Step 1 — mobile only  (identifies the customer, runs CreditSea dedupe)
 *   Step 2 — full KYC     (name, PAN, DOB, gender, pincode, income, employment)
 *
 * Submitting step 2 calls POST /api/creditsea/lead → backend orchestrates
 * dedupe + lead-push against CreditSea, records the click, and returns
 * a `redirect_url` where we send the user. That URL is either:
 *   • CreditSea's per-lead `utmLink` (deep-integration attribution), or
 *   • the fallback static URL with our click_id if CreditSea already had
 *     the lead / rejected the push.
 */
export default function CreditSeaSmartDialog({ open, onOpenChange, sharerId, metaPixelId, product }) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState("");
  const brandColor = normalizeColor(product?.brand_color);
  const buttonColor = safeButtonColor(product?.brand_color);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    pan: "",
    dob: "",           // user types DD-MM-YYYY, backend normalises
    gender: "",
    pincode: "",
    income: "",
    employmentType: "",
  });

  // Fire the CreditSea Smart Meta Pixel PageView when the dialog opens.
  useEffect(() => {
    if (open && metaPixelId) initMetaPixel(metaPixelId);
  }, [open, metaPixelId]);

  const reset = () => {
    setStep(1);
    setPhone("");
    setForm({
      first_name: "", last_name: "", pan: "", dob: "",
      gender: "", pincode: "", income: "", employmentType: "",
    });
  };

  const closeAll = (v) => {
    onOpenChange(v);
    if (!v) reset();
  };

  const goToStep2 = (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, "").slice(-10);
    if (digits.length !== 10 || !/^[6-9]/.test(digits)) {
      toast.error("Enter a valid 10-digit Indian mobile number");
      return;
    }
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/creditsea/lead", {
        ...form,
        phone,
        session_id: getSessionId(),
        sharer_id: sharerId || undefined,
      });
      // Persist HITCH-USER id if the identify step created a fresh one —
      // /creditsea/lead calls customer_identify internally so the cookie
      // is already set; we just mirror the id into localStorage.
      if (data?.hitch_user_id) setUserId(data.hitch_user_id);
      // Fire CreditSea Smart Meta Pixel Lead event on successful submission.
      if (metaPixelId) trackLead(metaPixelId, { content_name: "CreditSea Smart" });
      if (data?.dedupe_hit) {
        toast.info("Welcome back — CreditSea already has you. Redirecting…");
      } else if (data?.lead_id) {
        toast.success("Application submitted — redirecting to CreditSea…");
      } else if (data?.fallback) {
        toast.success("Redirecting to CreditSea…");
      }
      // Give the toast a moment before we blow the tab away.
      setTimeout(() => {
        window.location.href = data.redirect_url;
      }, 300);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit — please try again";
      toast.error(msg);
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={closeAll}>
      <DialogContent className="sm:max-w-md" data-testid="creditsea-smart-dialog">
        <DialogHeader className="text-left">
          <BrandHeader product={product} />
          <div className="inline-flex items-center gap-2 badge-mono text-neutral-500 mb-2">
            <Building2 className="w-3 h-3" />
            CreditSea Smart · Step {step} of 2
          </div>
          <DialogTitle className="font-display text-2xl font-bold tracking-tight">
            {step === 1 ? "Verify your mobile" : "A few quick details"}
          </DialogTitle>
          <DialogDescription className="text-neutral-600 text-sm">
            {step === 1
              ? "One number — we'll pre-fill everything on CreditSea so you skip re-entering."
              : "Fill once here, we push it to CreditSea. You only need to OTP-verify on their end."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <form onSubmit={goToStep2} className="space-y-4 pt-2">
            <div>
              <Label htmlFor="cs-phone" className="text-xs">Mobile number</Label>
              <div className="mt-1 flex items-center bg-neutral-50 border border-black/10 rounded-lg overflow-hidden focus-within:border-black transition-colors">
                <span className="px-3 text-neutral-500 text-sm border-r border-black/10">
                  <Phone className="w-3.5 h-3.5" />
                </span>
                <span className="px-2 text-neutral-500 text-sm">+91</span>
                <Input
                  id="cs-phone"
                  type="tel"
                  inputMode="numeric"
                  maxLength={13}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="98XXX XXXXX"
                  data-testid="cs-smart-phone"
                  autoFocus
                  className="flex-1 border-0 bg-transparent focus-visible:ring-0"
                />
              </div>
            </div>
            <Button
              type="submit"
              data-testid="cs-smart-next"
              className="w-full h-11 text-white rounded-lg text-sm font-semibold transition-opacity hover:opacity-90"
              style={{ backgroundColor: buttonColor }}
            >
              Continue <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <div className="text-[11px] text-neutral-500 inline-flex items-center gap-1.5 justify-center w-full">
              <ShieldCheck className="w-3 h-3" /> Data stays with CreditSea. No impact on credit score.
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={submit} className="space-y-3.5 pt-2 max-h-[65vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name">
                <Input
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  placeholder="Aarav"
                  data-testid="cs-smart-first-name"
                  required
                />
              </Field>
              <Field label="Last name">
                <Input
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  placeholder="Sharma"
                  data-testid="cs-smart-last-name"
                  required
                />
              </Field>
            </div>

            <Field label="PAN" icon={<IdCard className="w-3.5 h-3.5" />}>
              <Input
                value={form.pan}
                onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })}
                placeholder="ABCDE1234F"
                maxLength={10}
                data-testid="cs-smart-pan"
                required
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Date of birth" icon={<Calendar className="w-3.5 h-3.5" />}>
                <Input
                  value={form.dob}
                  onChange={(e) => setForm({ ...form, dob: e.target.value })}
                  placeholder="DD-MM-YYYY"
                  data-testid="cs-smart-dob"
                  required
                />
              </Field>
              <Field label="Gender">
                <select
                  value={form.gender}
                  onChange={(e) => setForm({ ...form, gender: e.target.value })}
                  data-testid="cs-smart-gender"
                  required
                  className="w-full h-9 px-3 rounded-md border border-black/10 bg-white text-sm focus:outline-none focus:border-black"
                >
                  <option value="">Select…</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Pincode" icon={<MapPin className="w-3.5 h-3.5" />}>
                <Input
                  value={form.pincode}
                  onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                  placeholder="560001"
                  inputMode="numeric"
                  maxLength={6}
                  data-testid="cs-smart-pincode"
                  required
                />
              </Field>
              <Field label="Monthly income" icon={<Wallet className="w-3.5 h-3.5" />}>
                <Input
                  value={form.income}
                  onChange={(e) => setForm({ ...form, income: e.target.value.replace(/\D/g, "") })}
                  placeholder="35000"
                  inputMode="numeric"
                  data-testid="cs-smart-income"
                  required
                />
              </Field>
            </div>

            <Field label="Employment type" icon={<Briefcase className="w-3.5 h-3.5" />}>
              <select
                value={form.employmentType}
                onChange={(e) => setForm({ ...form, employmentType: e.target.value })}
                data-testid="cs-smart-employment"
                required
                className="w-full h-9 px-3 rounded-md border border-black/10 bg-white text-sm focus:outline-none focus:border-black"
              >
                <option value="">Select…</option>
                <option value="salaried">Salaried</option>
                <option value="self-employed">Self-employed</option>
              </select>
            </Field>

            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => setStep(1)}
                data-testid="cs-smart-back"
                className="h-11 px-3 rounded-lg border border-black/10 text-sm text-neutral-600 hover:bg-neutral-50"
                disabled={busy}
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <Button
                type="submit"
                data-testid="cs-smart-submit"
                disabled={busy}
                className="flex-1 h-11 text-white rounded-lg text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-70"
                style={{ backgroundColor: busy ? "#000" : buttonColor }}
              >
                {busy ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Submitting…</>) : (<>Submit & continue <ArrowRight className="w-4 h-4 ml-1.5" /></>)}
              </Button>
            </div>
            <div className="text-[11px] text-neutral-500 inline-flex items-center gap-1.5 justify-center w-full pb-1">
              <ShieldCheck className="w-3 h-3" /> By continuing you agree to CreditSea's T&C and share these details with them.
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, icon, children }) {
  return (
    <div>
      <Label className="text-xs flex items-center gap-1 text-neutral-600">
        {icon}
        {label}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
